import type { DiffStatus } from '~/types/product-diff';

/**
 * The one place attribute-diff status styling is defined. A designer retunes the
 * five statuses here and every surface follows — row wash, badge, legend dot, band
 * ledger — because nothing else hardcodes a diff colour.
 *
 * Colours come from the semantic token scale (`success`/`warning`/`error`) rather
 * than the raw palette, so both themes resolve. `replaced` uses the `replaced`
 * token added for it: a rename is neither a loss nor a gain, and `info` sits too
 * close to `--primary`, which is already the Lily AI accent on the same row.
 *
 * One label per status, used by both the badge and the legend. `edited` reads as
 * "Value changed" everywhere: a row can also carry an operator-override badge
 * labelled "Edited", and two different claims must not share a word in one row.
 */
export const DIFF_STATUS_CFG: Record<
  DiffStatus,
  {
    /** Row background wash, or `''` for none. Faint by design — it has to tell a
     *  reviewer what kind of row this is at a glance without fighting the values
     *  printed on top of it. Only `unchanged` goes untinted: a row where nothing
     *  happened is the one case with nothing to signal. */
    rowBg: string;
    /** Badge beside the attribute name on the optimized side, and the chip in a
     *  band's status ledger. */
    badge: string;
    /** Legend dot — the badge's hue at full strength. */
    dot: string;
    /** Prefix in a band's status ledger (`+2`, `~1`, `⇄1`). */
    sign: string;
    /**
     * Suppresses the *badge* only — the row wash is unaffected. True where the row
     * already states the status twice over: `added` rows carry the green wash and
     * a "Not present" cell opposite the value, `unchanged` rows show the same value
     * on both sides. A third chip saying so lands on nearly every row of a real
     * product and crowds out the two or three that need judging. Both keep an
     * `sr-only` label, so nothing is lost non-visually.
     */
    quiet: boolean;
    /** Key under `productDetailsComparison.status` in the `common` namespace. */
    labelKey: string;
  }
> = {
  added: {
    rowBg: 'bg-success-50/60',
    badge: 'bg-success-50 text-success',
    dot: 'bg-success',
    sign: '+',
    quiet: true,
    labelKey: 'added',
  },
  edited: {
    rowBg: 'bg-warning-50/60',
    badge: 'bg-warning-50 text-warning',
    dot: 'bg-warning',
    sign: '~',
    quiet: false,
    labelKey: 'edited',
  },
  replaced: {
    rowBg: 'bg-replaced-50/60',
    badge: 'bg-replaced-50 text-replaced',
    dot: 'bg-replaced',
    sign: '⇄',
    quiet: false,
    labelKey: 'replaced',
  },
  removed: {
    rowBg: 'bg-error-50/60',
    badge: 'bg-error-50 text-error',
    dot: 'bg-error',
    sign: '−',
    quiet: false,
    labelKey: 'removed',
  },
  unchanged: {
    rowBg: '',
    badge: 'bg-neutral-20 text-muted-foreground',
    dot: 'bg-neutral-50',
    sign: '=',
    quiet: true,
    labelKey: 'unchanged',
  },
};

/**
 * Legend and ledger display order — most consequential first, `unchanged` last.
 * Not the Record's key order: object iteration order is an implementation
 * detail, and this sequence is a design decision.
 */
export const DIFF_STATUS_ORDER: DiffStatus[] = [
  'added',
  'edited',
  'replaced',
  'removed',
  'unchanged',
];
